import { GUEST_GRANT_PATH } from './apiRoutes'
import { isSafeRedirectUrl } from './tmAuthStorage'

export const GUEST_FLOW_TOKEN_HEADER = 'X-Guest-Flow-Token'
// Sent with OTP submissions; issued by the sponsor-status endpoint on approval.
export const GUEST_OTP_TOKEN_HEADER = 'X-Guest-Otp-Token'

/**
 * Shape check for a server-issued guest-flow token. Tokens are signed AND
 * encrypted by the backend (nested JWT -> compact JWE, 5 segments:
 * header.encryptedKey.iv.ciphertext.tag, encryptedKey is empty for "dir").
 *
 * The browser can neither read the claims nor verify the signature — only the
 * backend holds the keys and checks both on the next request. This only stops
 * the UI moving on when the token is plainly missing or malformed.
 */
export function isSignedToken(token) {
  return (
    typeof token === 'string' &&
    /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(
      token,
    )
  )
}

export class GuestGrantError extends Error {
  constructor(message, statusCode = null) {
    super(message)
    this.name = 'GuestGrantError'
    this.statusCode = statusCode
  }
}

async function readJson(response) {
  try {
    return await response.json()
  } catch {
    return {}
  }
}

/**
 * Final step of the guest flow.
 *
 * A correct OTP makes the backend return a short-lived (2 minute) grant token:
 * a server-signed JWT bound to the guest ID and device MAC. Only that token is
 * accepted by POST /v1/guestwifi/guest/grant, which re-checks the guest row
 * server-side and returns the redirect URL taken from the signed token.
 *
 * The UI must never redirect on a client-visible "Approved" status alone: that
 * response can be intercepted and edited. It only redirects to the URL returned
 * here, after the backend has validated the grant token.
 *
 * @param {Object} params
 * @param {string} params.grantToken - grant_token from the OTP verify response
 * @param {Function} [params.fetchImpl] - fetch override (tests)
 * @returns {Promise<string>} server-provided redirect URL
 * @throws {GuestGrantError} when the token is missing/rejected or the URL is unsafe
 */
export async function completeGuestGrant({ grantToken, fetchImpl } = {}) {
  if (!grantToken || typeof grantToken !== 'string') {
    throw new GuestGrantError('Missing grant token from OTP verification')
  }

  const doFetch = fetchImpl || window.fetch.bind(window)
  const response = await doFetch(GUEST_GRANT_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      [GUEST_FLOW_TOKEN_HEADER]: grantToken,
    },
  })

  const body = await readJson(response)
  if (!response.ok) {
    throw new GuestGrantError(
      body?.message || `guest grant failed: ${response.status}`,
      response.status,
    )
  }

  const redirectUrl = body?.redirect_url
  if (!isSafeRedirectUrl(redirectUrl)) {
    throw new GuestGrantError('Guest grant returned an unsafe redirect URL')
  }

  return redirectUrl
}
